import { Link, useNavigate } from "react-router-dom";
import { CATEGORIES } from "@/constants/categories";
import { useCategoryProductCounts } from "@/hooks/useProducts";

interface CategorySidebarProps {
  activeSlug?: string;
}

export const CategorySidebar = ({ activeSlug }: CategorySidebarProps) => {
  const navigate = useNavigate();
  const { data: counts } = useCategoryProductCounts();

  const totalCount = counts?.total ?? 0;
  const countFor = (slug: string) => counts?.categories[slug] ?? 0;

  const handleCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const slug = event.target.value;
    navigate(slug ? `/products/${slug}` : "/products");
  };

  return (
    <aside
      className="lg:sticky lg:top-28 lg:self-start min-w-0 w-full max-w-full"
      data-testid="category-sidebar"
    >
      <p className="font-mono-acc uppercase tracking-[0.2em] text-xs text-vg-gray mb-3 lg:mb-4">
        Categories
      </p>

      <div className="lg:hidden">
        <select
          value={activeSlug ?? ""}
          onChange={handleCategoryChange}
          className="w-full h-11 px-3 border border-black/10 bg-white font-mono-acc text-xs uppercase tracking-[0.15em] text-vg-ink focus:outline-none focus:ring-1 focus:ring-vg-navy focus:border-vg-navy appearance-none bg-[length:1rem] bg-[right_0.75rem_center] bg-no-repeat"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23787878' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
          }}
          aria-label="Filter by category"
          data-testid="category-select-mobile"
        >
          <option value="">All ({totalCount})</option>
          {CATEGORIES.map((category) => (
            <option key={category.slug} value={category.slug}>
              {category.name} ({countFor(category.slug)})
            </option>
          ))}
        </select>
      </div>

      <nav className="hidden lg:flex lg:flex-col gap-1">
        <Link
          to="/products"
          className={`px-4 py-3 border font-mono-acc text-xs uppercase tracking-[0.15em] transition-colors ${
            !activeSlug
              ? "bg-vg-navy text-white border-vg-navy"
              : "border-black/10 text-vg-gray hover:border-black/30 hover:text-vg-ink"
          }`}
          data-testid="category-link-all"
        >
          All ({totalCount})
        </Link>
        {CATEGORIES.map((category) => {
          const count = countFor(category.slug);
          const active = activeSlug === category.slug;

          return (
            <Link
              key={category.slug}
              to={`/products/${category.slug}`}
              className={`px-4 py-3 border font-mono-acc text-xs uppercase tracking-[0.15em] transition-colors ${
                active
                  ? "bg-vg-navy text-white border-vg-navy"
                  : "border-black/10 text-vg-gray hover:border-black/30 hover:text-vg-ink"
              }`}
              data-testid={`category-link-${category.slug}`}
            >
              {category.name} ({count})
            </Link>
          );
        })}
      </nav>
    </aside>
  );
};

export default CategorySidebar;
